import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import { Link as RouterLink } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import Paper from "@mui/material/Paper";
import { PageHeaderProps, BreadcrumbItem } from "../../types";
import { useTheme } from "@mui/material/styles";

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  breadcrumbs,
  action,
  actionLabel = "Add New",
  actionIcon = <AddIcon />,
  actionPath,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        p: 3,
        borderRadius: 2,
        background: isDarkMode
          ? "linear-gradient(145deg, #1a1a1a 0%, #242424 100%)"
          : "linear-gradient(145deg, #f9fafc 0%, #f1f5f9 100%)",
        boxShadow: isDarkMode ? "0 4px 20px rgba(0, 0, 0, 0.25)" : "none",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          mb: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom={Boolean(subtitle)}
            sx={{
              fontWeight: "bold",
              color: isDarkMode ? theme.palette.text.primary : undefined,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{
                opacity: isDarkMode ? 0.8 : undefined,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        {(action || actionPath) && (
          <Box sx={{ mt: { xs: 1, sm: 0 } }}>
            {action ? (
              action
            ) : (
              <Button
                component={RouterLink}
                to={actionPath || ""}
                variant="contained"
                color="primary"
                startIcon={actionIcon}
                sx={{
                  fontWeight: "medium",
                  boxShadow: isDarkMode
                    ? "0 4px 12px rgba(0, 0, 0, 0.4)"
                    : undefined,
                  "&:hover": {
                    boxShadow: isDarkMode
                      ? "0 6px 15px rgba(0, 0, 0, 0.5)"
                      : undefined,
                    filter: isDarkMode ? "brightness(1.2)" : undefined,
                  },
                }}
              >
                {actionLabel}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{
            "& .MuiBreadcrumbs-separator": {
              color: isDarkMode ? "rgba(255, 255, 255, 0.5)" : undefined,
            },
          }}
        >
          {breadcrumbs.map((crumb: BreadcrumbItem, index: number) => {
            const isLast = index === breadcrumbs.length - 1;

            return isLast ? (
              <Typography key={index} color="text.primary">
                {crumb.label}
              </Typography>
            ) : (
              <Link
                key={index}
                component={RouterLink}
                to={crumb.path}
                color="inherit"
                underline="hover"
                sx={{
                  color: isDarkMode ? "rgba(255, 255, 255, 0.7)" : undefined,
                  "&:hover": {
                    color: isDarkMode ? "rgba(255, 255, 255, 0.9)" : undefined,
                  },
                }}
              >
                {crumb.label}
              </Link>
            );
          })}
        </Breadcrumbs>
      )}
    </Paper>
  );
};

export default PageHeader;
